/**
 * EntryRow — одна строка плоского списка записей на главном экране (§4.1).
 *
 * Структура:
 *   [N.] WORK_TYPE
 *        PRJ-NN-NNN · Проект          H ч ✎ 🗑
 *
 * Edge-cases (зафиксировано assessment §4.1):
 *  - `entry.work_type_id == null` (custom): `work_type_text.toUpperCase()`.
 *  - `entry.project_id == null && project_code_raw` (unknown): `code_raw ⚠ · нет в системе`.
 *
 * §20/18.x — кнопка удаления вернулась прямо на строку. До этого удаление
 * было доступно только внутри wizard edit-mode (§4.2) — на мобиле рабочий
 * на это не натыкался. Теперь рядом с ✎ есть 🗑 → onDelete(entry).
 */
import { useTranslation } from "react-i18next";
import { AlertTriangle, Pencil, Trash2 } from "lucide-react";

import type { Project, TimesheetEntry } from "@/api/field-timesheet";
import { ReworkBadge } from "@/components/timesheet/ReworkBadge";
import { formatHoursHalf } from "@/lib/timesheet";

interface EntryRowProps {
  entry: TimesheetEntry;
  /** 1-based порядковый номер в списке. */
  index: number;
  projectsById: Map<number, Project>;
  /** workTypesById: id → name (для рендера лейбла «СВАРКА»). */
  workTypesById: Map<number, string>;
  onEdit: (entry: TimesheetEntry) => void;
  onDelete: (entry: TimesheetEntry) => void;
  /** §20/1.1 — flash-affordance: только что сохранённая запись подсвечивается
   * через keyframe `entry-row-flash` (см. `styles/app.css`). Заменяет
   * sonner-toast «Сохранено», который перекрывал primary CTA. */
  flash?: boolean;
}

export function EntryRow({
  entry,
  index,
  projectsById,
  workTypesById,
  onEdit,
  onDelete,
  flash = false,
}: EntryRowProps): React.ReactElement {
  const { t } = useTranslation();

  const workTypeLabel = resolveWorkTypeLabel(entry, workTypesById);
  const project = resolveProject(entry, projectsById, t);

  return (
    <li
      className={
        "relative flex items-stretch gap-3 py-3 pr-2 pl-4" + (flash ? " entry-row-flash" : "")
      }
      style={{ borderTop: "1px solid var(--rule-line)" }}
      data-testid="entry-row"
      data-index={index}
      data-flash={flash ? "true" : undefined}
    >
      <button
        type="button"
        onClick={() => entry.id !== null && entry.id !== undefined && onEdit(entry)}
        className="flex min-w-0 flex-1 items-start gap-3 text-left"
        style={{ minHeight: 56 }}
        aria-label={t("field.main.entries.editAria")}
        data-testid="entry-edit"
      >
        <span
          className="shrink-0 font-condensed text-[13px] tabular-nums"
          style={{
            color: "var(--fg-3)",
            fontVariantNumeric: "tabular-nums",
            width: 18,
            paddingTop: 1,
          }}
          aria-hidden="true"
        >
          {index}.
        </span>
        <span className="min-w-0 flex-1 leading-snug">
          <span
            className="block truncate font-condensed text-[14px] font-semibold uppercase tracking-[0.04em]"
            style={{ color: "var(--fg-1)" }}
            data-testid="entry-line-1"
          >
            {workTypeLabel}
          </span>
          <span
            className="mt-0.5 block truncate font-condensed text-[12px] uppercase tracking-[0.06em]"
            style={{ color: project.isRaw ? "#b45309" : "var(--fg-3)" }}
            data-testid="entry-line-2"
          >
            <span
              style={{
                color: project.isRaw ? "#b45309" : "var(--fg-2)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {project.code}
            </span>
            {project.isRaw && (
              <AlertTriangle
                className="ml-1 inline-block h-3 w-3 align-[-1px]"
                aria-hidden="true"
              />
            )}
            <span style={{ color: "var(--fg-4)" }}> · </span>
            <span>{project.label}</span>
          </span>
          <ReworkBadge isRework={entry.is_rework} note={entry.rework_note} className="mt-1" />
        </span>
        <span
          className="shrink-0 self-center font-condensed text-[15px] font-semibold tabular-nums"
          style={{
            color: "var(--fg-1)",
            fontVariantNumeric: "tabular-nums",
            minWidth: 44,
            textAlign: "right",
          }}
          data-testid="entry-hours"
        >
          {formatHoursHalf(entry.hours_half)}
        </span>
      </button>
      <button
        type="button"
        onClick={() => entry.id !== null && entry.id !== undefined && onEdit(entry)}
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center self-center"
        style={{ color: "var(--fg-3)" }}
        aria-label={t("field.main.entries.editAria")}
        data-testid="entry-edit-icon"
      >
        <Pencil className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => entry.id !== null && entry.id !== undefined && onDelete(entry)}
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center self-center"
        style={{ color: "var(--fg-3)" }}
        aria-label={t("field.main.entries.deleteAria")}
        data-testid="entry-delete-icon"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </button>
    </li>
  );
}

function resolveWorkTypeLabel(entry: TimesheetEntry, workTypesById: Map<number, string>): string {
  if (entry.work_type_id !== null && entry.work_type_id !== undefined) {
    const name = workTypesById.get(entry.work_type_id);
    if (name) return name.toUpperCase();
    return "—";
  }
  if (entry.work_type_text) return entry.work_type_text.toUpperCase();
  return "—";
}

interface ResolvedProject {
  code: string;
  label: string;
  isRaw: boolean;
  isSkipped: boolean;
}

function resolveProject(
  entry: TimesheetEntry,
  projectsById: Map<number, Project>,
  t: (k: string) => string,
): ResolvedProject {
  if (entry.project_code_raw) {
    return {
      code: entry.project_code_raw,
      label: t("field.main.entries.unknownProject"),
      isRaw: true,
      isSkipped: false,
    };
  }
  if (entry.project_id !== null && entry.project_id !== undefined) {
    const p = projectsById.get(entry.project_id);
    return {
      code: p?.code ?? `№ ${entry.project_id}`,
      label: p?.name ?? "—",
      isRaw: false,
      isSkipped: false,
    };
  }
  // §20/2.1 — оба NULL: рабочий осознанно пропустил проект.
  return {
    code: "—",
    label: t("field.main.entries.noProject"),
    isRaw: false,
    isSkipped: true,
  };
}
